/**
 * 共通マスタ: the starting material JMC keeps for every clinic.
 *
 * Two kinds of row, deliberately on one screen because the operator's question
 * is the same for both - 「クリニックに何を配っているか」 - and the difference is a
 * property of the row, said in the list rather than by splitting the navigation:
 *
 *   PICKED - 文体・トーン, 記事タイプ. Read live. Approve one and it is in every
 *   clinic's dropdown; correct it once and the correction is everywhere. These
 *   were hardcoded in two client files that had already drifted apart.
 *
 *   COPIED - ペルソナ雛形, CTA雛形. The clinic presses 使う and gets its own DRAFT
 *   to edit and approve. A later edit here does not reach a copy already taken,
 *   which is the whole reason for copying rather than referencing.
 *
 * DRAFT / 公開中 / 提供終了 rather than a delete: retiring stops a template being
 * offered and leaves every clinic that already took a copy alone. Deleting is
 * for a row written by mistake.
 */
import { confirmDialog } from '../components/AppDialog.tsx';
import { useMemo, useState } from 'react';
import { useLoaderData, useRevalidator } from 'react-router';
import { ApiError, del, patch, post } from '../api.ts';
import {
  Card,
  Empty,
  Pill,
  RowMenu,
  StatusPill,
  Table,
  jaDate,
  rowOpen,
} from '../components/ui.tsx';
import { Modal, ModalForm } from '../components/Modal.tsx';
import { Field, FieldGroup } from '../components/EditPanel.tsx';
import { Select } from '../components/Select.tsx';
import { Textarea } from '../components/Textarea.tsx';
import { ImageUpload } from '../components/ImageUpload.tsx';
import { KeywordInput } from '../components/KeywordInput.tsx';
import { AnimatePresence, listItem, m } from '../components/motion.tsx';
import { SkeletonRows, usePendingScreen } from '../components/Skeleton.tsx';
import { CTA_CHANNELS } from '../../../shared/cta-channels.ts';
import { COPIED_KINDS, KIND_LABEL, type PlatformDefaultKind } from '../../../shared/schemas/defaults.ts';

interface PlatformDefault {
  id: string;
  kind: PlatformDefaultKind;
  label: string;
  description: string;
  payload: Record<string, unknown>;
  clinic_types: string[];
  status: 'DRAFT' | 'APPROVED' | 'RETIRED';
  sort_order: number;
  /** A persona template's face, owned by the platform. See migration 091. */
  photo_id: string | null;
  updated_at: string;
}

const KINDS: PlatformDefaultKind[] = ['TONE', 'ARTICLE_TYPE', 'PERSONA', 'CTA'];

const text = (value: unknown): string => (typeof value === 'string' ? value : '');
const isCopied = (kind: PlatformDefaultKind) => COPIED_KINDS.includes(kind);

/** The blank form, so "new" and "reset" are the same thing. */
const EMPTY = {
  kind: 'PERSONA' as PlatformDefaultKind,
  label: '',
  description: '',
  clinicTypes: [] as string[],
  sortOrder: '0',
  // Picked kinds.
  value: '',
  // Persona.
  ageRange: '',
  gender: '',
  concerns: '',
  tone: '',
  notes: '',
  aiBrief: '',
  // CTA.
  heading: '',
  body: '',
  buttonLabel: '',
  channels: [] as string[],
};

type Form = typeof EMPTY;

function formFor(row: PlatformDefault): Form {
  const payload = row.payload;
  return {
    ...EMPTY,
    kind: row.kind,
    label: row.label,
    description: row.description,
    clinicTypes: row.clinic_types,
    sortOrder: String(row.sort_order),
    value: text(payload.value),
    ageRange: text(payload.ageRange),
    gender: text(payload.gender),
    concerns: text(payload.concerns),
    tone: text(payload.tone),
    notes: text(payload.notes),
    aiBrief: text(payload.aiBrief),
    heading: text(payload.heading),
    body: text(payload.body),
    buttonLabel: text(payload.buttonLabel),
    channels: Array.isArray(payload.channels)
      ? payload.channels.filter((entry): entry is string => typeof entry === 'string')
      : [],
  };
}

/**
 * The payload, shaped by the kind.
 *
 * Built here rather than sent as the whole form: the server refuses a field that
 * is not part of the kind (so a CTA's 見出し cannot ride along on a tone), and
 * that refusal should never be reachable from this screen.
 */
function payloadOf(form: Form): Record<string, unknown> {
  if (form.kind === 'TONE' || form.kind === 'ARTICLE_TYPE') {
    return { value: form.value.trim() || form.label.trim() };
  }
  if (form.kind === 'PERSONA') {
    return {
      label: form.label.trim(),
      ageRange: form.ageRange,
      gender: form.gender,
      concerns: form.concerns,
      tone: form.tone,
      notes: form.notes,
      aiBrief: form.aiBrief,
    };
  }
  return {
    name: form.label.trim(),
    heading: form.heading,
    body: form.body,
    buttonLabel: form.buttonLabel,
    channels: form.channels,
  };
}

/** One line of what this row actually carries, for the list. */
function summarise(row: PlatformDefault): string {
  if (row.kind === 'TONE' || row.kind === 'ARTICLE_TYPE') return text(row.payload.value);
  if (row.kind === 'PERSONA') {
    return (
      [text(row.payload.ageRange), text(row.payload.gender)].filter(Boolean).join('・') +
      (text(row.payload.concerns) ? `：${text(row.payload.concerns)}` : '')
    );
  }
  return text(row.payload.buttonLabel) || text(row.payload.heading);
}

export default function AdminDefaults() {
  const { items } = useLoaderData<{ items: PlatformDefault[] }>();
  const revalidator = useRevalidator();
  const pending = usePendingScreen();

  const [kindFilter, setKindFilter] = useState<PlatformDefaultKind | ''>('');
  const [open, setOpen] = useState<'new' | PlatformDefault | null>(null);
  const [viewing, setViewing] = useState<PlatformDefault | null>(null);
  const [form, setForm] = useState<Form>(EMPTY);
  const [busy, setBusy] = useState(false);

  const editing = open !== 'new' && open !== null ? open : null;
  const shown = useMemo(
    () => (kindFilter ? items.filter((row) => row.kind === kindFilter) : items),
    [items, kindFilter],
  );
  const count = (kind: PlatformDefaultKind) => items.filter((row) => row.kind === kind).length;

  const [aiError, setAiError] = useState<string | null>(null);
  const [drafting, setDrafting] = useState(false);

  /**
   * Ask the model to fill in the descriptive fields.
   *
   * The same prompt the clinic's own 「悩み・文体を下書き」 uses, so a template and a
   * clinic's persona are drafted by one instruction. Only the descriptive half:
   * the name stays the operator's, and 年代・性別 are filled in only where they
   * left a gap, because overwriting a deliberate answer with a guess is worse
   * than leaving one.
   */
  async function draft() {
    setDrafting(true);
    setAiError(null);
    try {
      const suggestion = await post<{
        ageRange: string;
        gender: string;
        concerns: string;
        tone: string;
        notes: string;
      }>('/admin/defaults/draft-persona', {
        label: form.label,
        ...(form.ageRange ? { ageRange: form.ageRange } : {}),
        ...(form.gender ? { gender: form.gender } : {}),
        ...(form.aiBrief ? { brief: form.aiBrief } : {}),
      });
      setForm((current) => ({
        ...current,
        ageRange: current.ageRange || suggestion.ageRange,
        gender: current.gender || suggestion.gender,
        concerns: suggestion.concerns,
        tone: suggestion.tone,
        notes: suggestion.notes,
      }));
    } catch (err) {
      setAiError(
        err instanceof ApiError ? [err.message, ...err.fields].join(' / ') : '生成できませんでした',
      );
    } finally {
      setDrafting(false);
    }
  }

  /** Draw the template's face. Saved immediately: reviewing an image is looking at it. */
  async function drawPortrait() {
    if (!editing) return;
    setDrafting(true);
    setAiError(null);
    try {
      await post(`/admin/defaults/${editing.id}/portrait`, {});
      await revalidator.revalidate();
    } catch (err) {
      setAiError(
        err instanceof ApiError ? [err.message, ...err.fields].join(' / ') : '生成できませんでした',
      );
    } finally {
      setDrafting(false);
    }
  }

  function start(target: 'new' | PlatformDefault) {
    setForm(target === 'new' ? EMPTY : formFor(target));
    setAiError(null);
    setViewing(null);
    setOpen(target);
  }

  const save = () => {
    const payload = {
      label: form.label,
      description: form.description,
      payload: payloadOf(form),
      clinicTypes: form.clinicTypes,
      sortOrder: Number(form.sortOrder) || 0,
    };
    if (editing) return patch(`/admin/defaults/${editing.id}`, payload);
    return post('/admin/defaults', { ...payload, kind: form.kind });
  };

  async function setStatus(row: PlatformDefault, status: 'DRAFT' | 'APPROVED' | 'RETIRED') {
    setBusy(true);
    try {
      await patch(`/admin/defaults/${row.id}`, { status });
      await revalidator.revalidate();
      setViewing((current) => (current && current.id === row.id ? { ...current, status } : current));
    } finally {
      setBusy(false);
    }
  }

  async function remove(row: PlatformDefault) {
    const ok = await confirmDialog({
      title: 'この雛形を削除しますか？',
      message: `「${row.label}」を削除します。クリニックがすでに取り込んだ内容は残りますが、この雛形は元に戻せません。提供を止めるだけなら「提供終了」を使ってください。`,
      confirmLabel: '削除する',
      danger: true,
    });
    if (!ok) return;
    await del(`/admin/defaults/${row.id}`);
    setViewing(null);
    await revalidator.revalidate();
  }

  const toggleChannel = (value: string) =>
    setForm((current) => ({
      ...current,
      channels: current.channels.includes(value)
        ? current.channels.filter((entry) => entry !== value)
        : [...current.channels, value],
    }));

  return (
    <Card
      title="共通マスタ"
      caption="すべてのクリニックに配る初期データです。トーンと記事タイプはそのまま選択肢として表示され、ペルソナとCTAは「使う」でクリニック側に複製されます。"
      actions={
        <button className="btn btn--sm" type="button" onClick={() => start('new')}>
          ＋ 新規作成
        </button>
      }
    >
      <div className="chips tenantchips">
        <button
          type="button"
          className="chip"
          aria-pressed={kindFilter === ''}
          onClick={() => setKindFilter('')}
        >
          すべて（{items.length}）
        </button>
        {KINDS.map((kind) => (
          <button
            key={kind}
            type="button"
            className="chip"
            aria-pressed={kindFilter === kind}
            onClick={() => setKindFilter(kindFilter === kind ? '' : kind)}
          >
            {KIND_LABEL[kind]}（{count(kind)}）
          </button>
        ))}
      </div>

      {pending ? (
        <SkeletonRows rows={5} columns={5} />
      ) : shown.length === 0 ? (
        <Empty>
          まだ登録がありません。「＋ 新規作成」から、全クリニックに配る雛形を追加できます。
        </Empty>
      ) : (
        <Table
          head={
            <>
              <th scope="col">種類</th>
              <th scope="col">名称</th>
              <th scope="col">内容</th>
              <th scope="col">状態</th>
              <th scope="col">更新</th>
              <th scope="col">操作</th>
            </>
          }
        >
          {/*
            Default mode, not popLayout: popLayout lifts a leaving item out with
            position:absolute, and a table row taken out of its table loses its
            column widths and squashes. Here it fades where it is, then the rows
            below glide up (listItem's layout spring).
          */}
          <AnimatePresence>
            {shown.map((row, index) => (
              <m.tr
                key={row.id}
                data-motion
                layout
                {...listItem(index)}
                {...rowOpen(() => setViewing(row))}
              >
                <td>
                  <span className="chips">
                    <Pill tone="muted">{KIND_LABEL[row.kind]}</Pill>
                    {/* Which mechanism this row uses, said once per row rather
                        than explained in a paragraph nobody reads twice. */}
                    {isCopied(row.kind) ? (
                      <Pill>複製</Pill>
                    ) : (
                      <Pill tone="ok">選択肢</Pill>
                    )}
                  </span>
                </td>
                <td>{row.label}</td>
                <td className="muted">
                  <span className="cellclamp">{summarise(row) || '—'}</span>
                </td>
                <td>
                  <StatusPill status={row.status} />
                </td>
                <td className="muted">{jaDate(row.updated_at)}</td>
                <td>
                  <div className="rowactions">
                    <RowMenu>
                      <button type="button" onClick={() => setViewing(row)}>
                        内容を確認
                      </button>
                      <button type="button" onClick={() => start(row)}>
                        編集
                      </button>
                      {row.status !== 'APPROVED' ? (
                        <button type="button" onClick={() => void setStatus(row, 'APPROVED')}>
                          公開する
                        </button>
                      ) : (
                        <button type="button" onClick={() => void setStatus(row, 'RETIRED')}>
                          提供を終了する
                        </button>
                      )}
                      <button type="button" onClick={() => void remove(row)}>
                        完全に削除
                      </button>
                    </RowMenu>
                  </div>
                </td>
              </m.tr>
            ))}
          </AnimatePresence>
        </Table>
      )}

      <Modal
        open={viewing !== null}
        onClose={() => setViewing(null)}
        title={viewing?.label ?? ''}
        caption={
          viewing && isCopied(viewing.kind)
            ? 'クリニックが「使う」を押すと、この内容が下書きとして複製されます。'
            : 'クリニックの選択肢にそのまま表示されます。'
        }
        wide
      >
        {viewing && (
          <>
            <div className="chips" style={{ marginBottom: 10 }}>
              <Pill tone="muted">{KIND_LABEL[viewing.kind]}</Pill>
              <StatusPill status={viewing.status} />
              {viewing.clinic_types.map((type) => (
                <span key={type} className="tag">
                  {type}
                </span>
              ))}
            </div>
            {viewing.description && <p className="muted small">{viewing.description}</p>}
            <dl className="psheet__facts">
              {Object.entries(viewing.payload).map(([key, value]) => (
                <div key={key}>
                  <dt>{key}</dt>
                  <dd>
                    {Array.isArray(value)
                      ? value.join('、')
                      : text(value) || <span className="muted">未記入</span>}
                  </dd>
                </div>
              ))}
            </dl>
            <div className="editpanel__actions">
              <button className="btn btn--sm" type="button" onClick={() => start(viewing)}>
                編集する
              </button>
              {viewing.status !== 'APPROVED' ? (
                <button
                  className="btn btn--ghost btn--sm"
                  type="button"
                  disabled={busy}
                  onClick={() => void setStatus(viewing, 'APPROVED')}
                >
                  公開する
                </button>
              ) : (
                <button
                  className="btn btn--ghost btn--sm"
                  type="button"
                  disabled={busy}
                  onClick={() => void setStatus(viewing, 'RETIRED')}
                >
                  提供を終了する
                </button>
              )}
              {viewing.status !== 'APPROVED' && (
                <span className="muted small">公開するまで、クリニックには表示されません。</span>
              )}
            </div>
          </>
        )}
      </Modal>

      <ModalForm
        open={open !== null}
        onClose={() => setOpen(null)}
        title={editing ? '共通マスタを編集' : '共通マスタを作成'}
        caption={
          editing
            ? '保存しても公開状態は変わりません。既にクリニックが複製した内容には影響しません。'
            : '作成した内容は下書きです。公開するまでクリニックには表示されません。'
        }
        wide
        onSubmit={save}
        submitLabel={editing ? '保存する' : '作成する'}
      >
        <FieldGroup title="種類" note="種類によって、クリニックでの使われ方が変わります" />
        <Field
          label="種類"
          htmlFor="pd-kind"
          span="wide"
          hint={
            isCopied(form.kind)
              ? 'クリニックが「使う」を押すと下書きとして複製され、以降はクリニックのものになります。'
              : 'クリニックの選択肢にそのまま表示されます。あとから修正すると全クリニックに反映されます。'
          }
        >
          <Select
            id="pd-kind"
            value={form.kind}
            // The kind decides which columns the payload has, so changing it on
            // an existing row would leave fields nothing reads.
            disabled={editing !== null}
            onChange={(kind) => setForm({ ...form, kind: kind as PlatformDefaultKind })}
            options={KINDS.map((kind) => ({ value: kind, label: KIND_LABEL[kind] }))}
          />
        </Field>

        <Field label="表示順" htmlFor="pd-sort" hint="小さいほど先に表示されます。">
          <input
            id="pd-sort"
            type="number"
            min={0}
            max={9999}
            value={form.sortOrder}
            onChange={(e) => setForm({ ...form, sortOrder: e.target.value })}
          />
        </Field>

        <Field
          label="名称"
          htmlFor="pd-label"
          required
          span="wide"
          hint={
            form.kind === 'PERSONA'
              ? 'クリニックの一覧に表示される見出し。例：40代・たるみが気になる'
              : '一覧に表示される名前です。'
          }
        >
          <input
            id="pd-label"
            value={form.label}
            maxLength={120}
            required
            onChange={(e) => setForm({ ...form, label: e.target.value })}
          />
        </Field>

        <Field
          label="説明"
          htmlFor="pd-desc"
          span="full"
          hint="どんなクリニック向けか、どう使うか。クリニック側の提案カードにも表示されます。"
        >
          <input
            id="pd-desc"
            value={form.description}
            maxLength={500}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
          />
        </Field>

        <Field
          label="対象のクリニック種別"
          htmlFor="pd-types"
          span="full"
          hint="例：美容皮膚科、美容外科。空欄ならすべてのクリニックに表示されます。"
        >
          <KeywordInput
            id="pd-types"
            value={form.clinicTypes}
            onChange={(clinicTypes) => setForm({ ...form, clinicTypes })}
          />
        </Field>

        {(form.kind === 'TONE' || form.kind === 'ARTICLE_TYPE') && (
          <>
            <FieldGroup title="内容" note="実際に記事へ渡される値です" />
            <Field
              label="値"
              htmlFor="pd-value"
              span="full"
              hint={
                form.kind === 'TONE'
                  ? 'AIに渡される文体の指示そのもの。空欄なら名称がそのまま使われます。例：やさしく、専門用語は避けて言い換える'
                  : '保存される値。例：blog / column / case。既存の記事に保存された値は変わりません。'
              }
            >
              <input
                id="pd-value"
                value={form.value}
                maxLength={120}
                placeholder={form.label}
                onChange={(e) => setForm({ ...form, value: e.target.value })}
              />
            </Field>
          </>
        )}

        {form.kind === 'PERSONA' && (
          <>
            <FieldGroup
              title="ペルソナの内容"
              note="クリニックが複製したあと自由に編集できます"
            />

            <Field
              label="AIへの指示"
              htmlFor="pd-brief"
              span="full"
              hint="「共働き」「初回カウンセリング前」など。下の下書きボタンの手がかりになり、複製先にもそのまま引き継がれます。"
            >
              <input
                id="pd-brief"
                value={form.aiBrief}
                maxLength={1000}
                onChange={(e) => setForm({ ...form, aiBrief: e.target.value })}
              />
            </Field>

            {/*
              Inside the form but not a submit: these fill the fields below and
              the operator still has to read them and press save. Same pair as
              the clinic's own persona screen.
            */}
            <div className="aiassist field--full">
              <div className="aiassist__buttons">
                <button
                  className="btn btn--sm"
                  type="button"
                  disabled={drafting || !form.label.trim()}
                  onClick={() => void draft()}
                >
                  {drafting ? '生成中…' : '✦ 悩み・文体を下書き'}
                </button>
                {editing && (
                  <button
                    className="btn btn--sm btn--ghost"
                    type="button"
                    disabled={drafting}
                    onClick={() => void drawPortrait()}
                  >
                    ✦ 顔写真を生成
                  </button>
                )}
              </div>
              <span className="aiassist__note">
                名称と上の指示をもとに、下の項目を埋めます。JMCの費用で実行され、クリニックの
                利用量には計上されません。
              </span>
              {aiError && (
                <p className="alert" role="alert">
                  {aiError}
                </p>
              )}
            </div>

            <Field label="年代" htmlFor="pd-age">
              <input
                id="pd-age"
                value={form.ageRange}
                maxLength={40}
                onChange={(e) => setForm({ ...form, ageRange: e.target.value })}
              />
            </Field>
            <Field label="性別" htmlFor="pd-gender">
              <input
                id="pd-gender"
                value={form.gender}
                maxLength={40}
                onChange={(e) => setForm({ ...form, gender: e.target.value })}
              />
            </Field>
            <Field
              label="悩み・関心"
              htmlFor="pd-concerns"
              span="full"
              hint="具体的なほど良い雛形になります。ただしクリニック固有の事実は書かないでください。"
            >
              <Textarea
                id="pd-concerns"
                minRows={4}
                value={form.concerns}
                maxLength={2000}
                onChange={(e) => setForm({ ...form, concerns: e.target.value })}
              />
            </Field>
            <Field label="希望トーン" htmlFor="pd-tone" span="wide">
              <input
                id="pd-tone"
                value={form.tone}
                maxLength={200}
                onChange={(e) => setForm({ ...form, tone: e.target.value })}
              />
            </Field>
            <Field label="備考" htmlFor="pd-notes" span="full">
              <Textarea
                id="pd-notes"
                minRows={3}
                value={form.notes}
                maxLength={2000}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
              />
            </Field>

            {editing && (
              <Field
                label="顔写真"
                htmlFor="pd-photo"
                span="full"
                hint="複製したクリニックにもこの写真が引き継がれます。架空の人物のイメージで、実在の患者の写真は使用しないでください。"
              >
                <ImageUpload
                  imageId={editing.photo_id}
                  endpoint={`/admin/defaults/${editing.id}/photo`}
                  label="画像を選ぶ"
                  placeholder={form.label.trim().charAt(0) || '人'}
                  onDone={() => revalidator.revalidate()}
                />
              </Field>
            )}
          </>
        )}

        {form.kind === 'CTA' && (
          <>
            <FieldGroup
              title="CTAの内容"
              note="リンク先はクリニックが入力します（共通マスタには持たせません）"
            />
            <Field label="ボタン文言" htmlFor="pd-btn" span="wide">
              <input
                id="pd-btn"
                value={form.buttonLabel}
                maxLength={60}
                onChange={(e) => setForm({ ...form, buttonLabel: e.target.value })}
              />
            </Field>
            <Field label="見出し" htmlFor="pd-heading" span="full">
              <input
                id="pd-heading"
                value={form.heading}
                maxLength={200}
                onChange={(e) => setForm({ ...form, heading: e.target.value })}
              />
            </Field>
            <Field label="本文" htmlFor="pd-body" span="full">
              <Textarea
                id="pd-body"
                minRows={3}
                value={form.body}
                maxLength={1000}
                onChange={(e) => setForm({ ...form, body: e.target.value })}
              />
            </Field>
            <div className="field field--full">
              <span className="field__label">予約手段</span>
              <div className="chips">
                {CTA_CHANNELS.map((channel) => (
                  <button
                    key={channel.value}
                    type="button"
                    className="chip"
                    aria-pressed={form.channels.includes(channel.value)}
                    onClick={() => toggleChannel(channel.value)}
                  >
                    {channel.label}
                  </button>
                ))}
              </div>
            </div>
          </>
        )}
      </ModalForm>
    </Card>
  );
}
